import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return ''; }
};

const ChatPanel = ({ roomId, token, role }) => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const bodyRef = useRef(null);
  const lastSeenRef = useRef(0);
  const isNearBottomRef = useRef(true);

  const load = async () => {
    try {
      const r = await classroomApi.chatList(roomId, token);
      const next = r.items || [];
      const nextCount = next.length;
      if (nextCount > lastSeenRef.current && !isNearBottomRef.current) {
        setUnread((u) => u + (nextCount - lastSeenRef.current));
      }
      setItems(next);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, token]);

  useEffect(() => {
    if (!bodyRef.current) return;
    if (isNearBottomRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      lastSeenRef.current = items.length;
      setUnread(0);
    }
  }, [items.length]);

  const onScroll = () => {
    if (!bodyRef.current) return;
    const el = bodyRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    isNearBottomRef.current = nearBottom;
    if (nearBottom) {
      lastSeenRef.current = items.length;
      setUnread(0);
    }
  };

  const scrollToBottom = () => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    isNearBottomRef.current = true;
    lastSeenRef.current = items.length;
    setUnread(0);
  };

  const send = async () => {
    const msg = draft.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      const r = await classroomApi.chatPost(roomId, token, msg);
      setItems((prev) => [...prev, r]);
      setDraft('');
      // Force scroll to bottom after own message
      setTimeout(scrollToBottom, 0);
    } catch (e) { toast.error(e.message || 'Could not send'); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-2xl overflow-hidden relative" data-testid="classroom-chat">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-2">
          In-class chat
          {unread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-black" data-testid="classroom-chat-unread">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
        <div className="text-[10px] text-gray-400">Auto-refreshes</div>
      </div>
      <div ref={bodyRef} onScroll={onScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[260px] max-h-[420px]">
        {items.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-10">No messages yet. Say hello.</div>
        )}
        {items.map((m, i) => (
          <div key={i} className={`flex ${m.role === role ? 'justify-end' : 'justify-start'}`}
            data-testid={`classroom-chat-msg-${i}`}>
            <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${m.role === role ? 'bg-black text-white' : 'bg-gray-100 text-gray-900'}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5 flex items-center justify-between gap-2">
                <span>{m.user_name} · {m.role}</span>
                {m.at && <span className="opacity-60" data-testid={`classroom-chat-ts-${i}`}>{fmtTs(m.at)}</span>}
              </div>
              <div className="whitespace-pre-wrap break-words">{m.message}</div>
            </div>
          </div>
        ))}
      </div>
      {unread > 0 && (
        <button onClick={scrollToBottom}
          className="absolute bottom-16 right-4 h-8 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-wider shadow"
          data-testid="classroom-chat-jump">
          ↓ {unread} new
        </button>
      )}
      <div className="border-t border-gray-100 p-3 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message… (Shift+Enter for newline)"
          className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-red-600"
          data-testid="classroom-chat-input"
        />
        <button onClick={send} disabled={sending || !draft.trim()}
          className="h-10 w-10 rounded-full bg-red-700 hover:bg-red-800 text-white flex items-center justify-center disabled:opacity-50"
          data-testid="classroom-chat-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
